/**
 * Browser half of the localhost backend switcher (see backends.ts). The pick
 * lives in localStorage and is read once per page load: switching writes it
 * and reloads, so every request a page makes goes to the same backend.
 *
 * While a remote backend is active, `installDevBackendRelay` (run by
 * install.ts before the app loads) rewrites same-origin /api and /ws URLs
 * passed to fetch and WebSocket onto the relay prefix. URLs the browser loads
 * by itself (an <audio src>, `new Audio(...)`) must go through `backendUrl`.
 */
import {
  REMOTE_DEV_BACKENDS,
  isBackendPath,
  isLoopbackHostname,
  relayPrefix,
  remoteDevBackend,
  type DevBackendId,
  type RemoteDevBackend,
} from './backends';

export const DEV_BACKEND_STORAGE_KEY = 'voxcore.devBackend';

/** Only offered on localhost; deployed frontends always use their own backend. */
export function isDevBackendSwitchAvailable(): boolean {
  return typeof window !== 'undefined' && isLoopbackHostname(window.location.hostname);
}

function readStoredBackend(): DevBackendId {
  if (!isDevBackendSwitchAvailable()) return 'local';
  try {
    return remoteDevBackend(window.localStorage.getItem(DEV_BACKEND_STORAGE_KEY))?.id ?? 'local';
  } catch {
    return 'local';
  }
}

let active: DevBackendId | undefined;

/** The backend this page load runs against. */
export function activeDevBackend(): DevBackendId {
  if (active === undefined) active = readStoredBackend();
  return active;
}

export function activeRemoteBackend(): RemoteDevBackend | null {
  return remoteDevBackend(activeDevBackend());
}

/** Test hook: forget the value read for this page load. */
export function resetDevBackendCache(): void {
  active = undefined;
}

export function switchDevBackend(id: DevBackendId, reload: () => void = () => window.location.reload()): void {
  try {
    if (id === 'local') window.localStorage.removeItem(DEV_BACKEND_STORAGE_KEY);
    else window.localStorage.setItem(DEV_BACKEND_STORAGE_KEY, id);
  } catch {
    return;
  }
  reload();
}

/**
 * Moves a same-origin /api or /ws URL onto the relay for `backend`. Relative
 * URLs stay relative; other origins and frontend paths pass through.
 */
export function toRelayUrl(input: string, backend: RemoteDevBackend, pageHref: string): string {
  let url: URL;
  let page: URL;
  try {
    url = new URL(input, pageHref);
    page = new URL(pageHref);
  } catch {
    return input;
  }
  const isWs = url.protocol === 'ws:' || url.protocol === 'wss:';
  const sameOrigin = isWs ? url.host === page.host : url.origin === page.origin;
  if (!sameOrigin || !isBackendPath(url.pathname)) return input;

  const relayed = `${relayPrefix(backend)}${url.pathname}${url.search}${url.hash}`;
  const isPathOnly = input.startsWith('/') && !input.startsWith('//');
  return isPathOnly ? relayed : `${url.protocol}//${url.host}${relayed}`;
}

/** `path` as this page load should request it: relayed while a remote backend is active. */
export function backendUrl(path: string): string {
  const backend = activeRemoteBackend();
  return backend ? toRelayUrl(path, backend, window.location.href) : path;
}

/**
 * The origin the backend should treat as the app's public address (webhook
 * base URLs it registers with third parties). While relaying that is the
 * deployment's own origin, never this localhost page.
 */
export function appPublicOrigin(): string {
  return activeRemoteBackend()?.origin ?? window.location.origin;
}

let installed = false;

export function installDevBackendRelay(): void {
  const backend = activeRemoteBackend();
  if (!backend || installed) return;
  installed = true;

  const relay = (url: string) => toRelayUrl(url, backend, window.location.href);

  const nativeFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string') return nativeFetch(relay(input), init);
    if (input instanceof URL) return nativeFetch(relay(input.href), init);
    const relayed = relay(input.url);
    return nativeFetch(relayed === input.url ? input : new Request(relayed, input), init);
  }) as typeof window.fetch;

  // A Proxy rather than a subclass: statics (WebSocket.OPEN) and instanceof
  // keep working, and it doesn't depend on how classes get transpiled.
  window.WebSocket = new Proxy(window.WebSocket, {
    construct(target, [url, ...rest]: ConstructorParameters<typeof WebSocket>) {
      return Reflect.construct(target, [relay(String(url)), ...rest]);
    },
  });

  console.info(`[devBackend] ${backend.label} backend: /api and /ws go to ${backend.origin} via the dev server relay`);
}

export { REMOTE_DEV_BACKENDS };
export type { DevBackendId, RemoteDevBackend };
